import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { Role, Roles } from "../models/Role";
import {
  ReportType,
  ServerReportType,
} from "@modules/questionnaire-builder/ReportType";

const CONTRACT_TYPE_MAP: Record<ServerReportType, ReportType> = {
  npp: ReportType.INITIAL,
  npp_ortho_spine: ReportType.INITIAL,
  npp_ortho_extremities: ReportType.INITIAL_ORTHO_EXTREMITIES,
  npp_primary_care: ReportType.INITIAL,
  followup: ReportType.FOLLOW_UP,
  followup_ortho_spine: ReportType.FOLLOW_UP,
  followup_ortho_extremities: ReportType.FOLLOW_UP_ORTHO_EXTREMITIES,
  followup_primary_care: ReportType.FOLLOW_UP,
  reeval: ReportType.FOLLOW_UP,
  reeval_ortho_spine: ReportType.FOLLOW_UP,
  reeval_ortho_extremities: ReportType.FOLLOW_UP_ORTHO_EXTREMITIES,
};

const mapServerToClientReportType = (type: ServerReportType): ReportType => {
  return CONTRACT_TYPE_MAP[type];
};

// Define the shape of the context value
interface PatientDataContextValue {
  verificationCodes: string;
  setVerificationCodes: React.Dispatch<React.SetStateAction<string>>;
  patientId: number;
  setPatientId: React.Dispatch<React.SetStateAction<number>>;
  facilityId: number;
  setFacilityId: React.Dispatch<React.SetStateAction<number>>;
  contractId?: number | string;
  setContractId: React.Dispatch<
    React.SetStateAction<number | string | undefined>
  >;
  viewedContractIds?: number[];
  setViewedContractIds: React.Dispatch<React.SetStateAction<number[]>>;
  contractTitle?: string;
  setContractTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
  contractInjuryDate?: string;
  setContractInjuryDate: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  contractServiceDate?: string;
  setContractServiceDate: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  sessionId: string;
  setSessionId: React.Dispatch<React.SetStateAction<string>>;
  role: Role;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  contract: {
    type: ReportType;
  };
  updateContractType: (type: ServerReportType) => void;
}

const PatientDataContext = createContext<PatientDataContextValue | undefined>(
  undefined,
);

export const usePatientDataContext = (): PatientDataContextValue => {
  const context = useContext(PatientDataContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
// move this somewhere else
interface ContextProviderProps {
  children: ReactNode;
}

export const PatientDataProvider: React.FC<ContextProviderProps> = ({
  children,
}) => {
  const [verificationCodes, setVerificationCodes] = useState<string>("");
  const [patientId, setPatientId] = useState<number>(0);
  const [facilityId, setFacilityId] = useState<number>(0);
  const [contractId, setContractId] = useState<number | string | undefined>(
    undefined,
  );
  const [viewedContractIds, setViewedContractIds] = useState<number[]>([]);
  const [contractTitle, setContractTitle] = useState<string | undefined>(
    undefined,
  );
  const [contractInjuryDate, setContractInjuryDate] = useState<
    string | undefined
  >(undefined);
  const [contractServiceDate, setContractServiceDate] = useState<
    string | undefined
  >(undefined);
  const [sessionId, setSessionId] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [role] = useState<Role>(Roles.PATIENT);
  const [contract, setContract] = useState<{ type: ReportType }>({
    type: ReportType.INITIAL,
  });

  const updateContractType = useCallback((type: ServerReportType) => {
    setContract({
      type: mapServerToClientReportType(type),
    });
  }, []);

  const contextValue: PatientDataContextValue = {
    verificationCodes,
    setVerificationCodes,
    patientId,
    setPatientId,
    facilityId,
    setFacilityId,
    contractId,
    setContractId,
    viewedContractIds,
    setViewedContractIds,
    contractTitle,
    setContractTitle,
    contractInjuryDate,
    setContractInjuryDate,
    contractServiceDate,
    setContractServiceDate,
    sessionId,
    setSessionId,
    role,
    phoneNumber,
    setPhoneNumber,
    contract,
    updateContractType,
  };

  return (
    <PatientDataContext.Provider value={contextValue}>
      {children}
    </PatientDataContext.Provider>
  );
};
